/**
 * ColorPicker Component
 * 
 * Обертка вокруг react-colorful с интеграцией в shadcn/ui дизайн систему
 * Поддерживает выбор цвета через визуальный picker и ввод hex, rgb, hsl значений
 */

import React, { useState, useCallback, useEffect } from 'react';
import { HexColorPicker, HslColorPicker, RgbColorPicker } from 'react-colorful';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Palette, Type, Pipette } from 'lucide-react';
import { cn } from '@/lib/utils';

// Типы для различных форматов цветов
export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

export interface HslColor {
  h: number;
  s: number;
  l: number;
}

export type ColorFormat = 'hex' | 'rgb' | 'hsl';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  format?: ColorFormat;
  className?: string;
  disabled?: boolean;
  showFormatTabs?: boolean;
  showTextInput?: boolean;
  presetColors?: string[];
}

// Утилиты для конвертации цветов
const hexToRgb = (hex: string): RgbColor | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
};

const hexToHsl = (hex: string): HslColor => {
  const rgb = hexToRgb(hex);
  if (!rgb) return { h: 0, s: 0, l: 0 };
  
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

const hslToHex = (h: number, s: number, l: number): string => {
  h /= 360;
  s /= 100;
  l /= 100;
  
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return rgbToHex(
    Math.round(r * 255),
    Math.round(g * 255),
    Math.round(b * 255)
  );
};

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  format = 'hex',
  className,
  disabled = false,
  showFormatTabs = true,
  showTextInput = true,
  presetColors = []
}) => {
  const [currentFormat, setCurrentFormat] = useState<ColorFormat>(format);
  const [textInput, setTextInput] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  
  // Синхронизируем text input с value
  useEffect(() => {
    setTextInput(value);
  }, [value]);

  // Обработчики для различных форматов
  const handleHexChange = useCallback((newColor: string) => {
    onChange(newColor);
    setTextInput(newColor);
  }, [onChange]);

  const handleRgbChange = useCallback((rgb: RgbColor) => {
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
    setTextInput(hex);
  }, [onChange]);

  const handleHslChange = useCallback((hsl: HslColor) => {
    const hex = hslToHex(hsl.h, hsl.s, hsl.l);
    onChange(hex);
    setTextInput(hex);
  }, [onChange]);

  const handleTextInputChange = useCallback((inputValue: string) => {
    setTextInput(inputValue);
    
    // Проверяем валидность и обновляем значение
    const hexRegex = /^#([0-9A-F]{3}){1,2}$/i;
    if (hexRegex.test(inputValue)) {
      onChange(inputValue);
    }
  }, [onChange]);

  // Текущие значения в разных форматах
  const currentRgb = hexToRgb(value) || { r: 0, g: 0, b: 0 };
  const currentHsl = hexToHsl(value);

  return (
    <div className={cn("space-y-3", className)}>
      {/* Кнопка-триггер с preview цвета */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              "h-10 px-3 py-2"
            )}
          >
            <div className="flex items-center gap-2 flex-1">
              <div 
                className="w-4 h-4 rounded-sm border flex-shrink-0"
                style={{ backgroundColor: value }}
              />
              <span className="flex-1 truncate font-mono text-sm">
                {value || 'Выберите цвет'}
              </span>
              <Pipette size={16} className="text-muted-foreground flex-shrink-0" />
            </div>
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-64 p-3" align="start">
          <div className="space-y-4">
            {/* Preset цвета */}
            {presetColors.length > 0 && (
              <div>
                <Label className="text-xs font-medium text-muted-foreground">
                  Быстрый выбор
                </Label>
                <div className="flex flex-wrap gap-1 mt-2">
                  {presetColors.map((presetColor) => (
                    <button
                      key={presetColor}
                      type="button"
                      onClick={() => handleHexChange(presetColor)}
                      className={cn(
                        "w-6 h-6 rounded-sm border-2 hover:scale-110 transition-transform",
                        value === presetColor 
                          ? "border-foreground shadow-md" 
                          : "border-border hover:border-foreground/50"
                      )}
                      style={{ backgroundColor: presetColor }}
                      title={presetColor}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Tabs для разных форматов */}
            {showFormatTabs ? (
              <Tabs value={currentFormat} onValueChange={(value) => setCurrentFormat(value as ColorFormat)}>
                <TabsList className="grid w-full grid-cols-3 text-xs">
                  <TabsTrigger value="hex" className="text-xs">HEX</TabsTrigger>
                  <TabsTrigger value="rgb" className="text-xs">RGB</TabsTrigger>
                  <TabsTrigger value="hsl" className="text-xs">HSL</TabsTrigger>
                </TabsList>
                
                <TabsContent value="hex" className="space-y-3">
                  <HexColorPicker color={value} onChange={handleHexChange} />
                </TabsContent>
                
                <TabsContent value="rgb" className="space-y-3">
                  <RgbColorPicker color={currentRgb} onChange={handleRgbChange} />
                </TabsContent>
                
                <TabsContent value="hsl" className="space-y-3">
                  <HslColorPicker color={currentHsl} onChange={handleHslChange} />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="space-y-3">
                <HexColorPicker color={value} onChange={handleHexChange} />
              </div>
            )}

            {/* Text input для прямого ввода */}
            {showTextInput && (
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground">
                  Точное значение
                </Label>
                <Input
                  type="text"
                  value={textInput}
                  onChange={(e) => handleTextInputChange(e.target.value)}
                  placeholder="#FF5733"
                  className="font-mono text-sm h-8"
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Вспомогательный компонент для простого выбора цвета
export const SimpleColorPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  className?: string;
}> = ({ value, onChange, disabled, className }) => {
  return (
    <ColorPicker
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={className}
      showFormatTabs={false}
      showTextInput={false}
    />
  );
};